/**
 * What a staff member is offered on a case, from their features and the case
 * as the server last sent it. The server decides again on every write
 * (server/connect-wallyo-service/console/support-cases.ts); these rules only
 * keep the screen from offering what would be refused.
 */
import type { StaffFeatureId } from "@wallyo/contracts/actions";
import { TEAM_SCREEN_FEATURES, type GetStaffMeResponse } from "@wallyo/contracts/console";
import type { SupportCaseDetail } from "@wallyo/contracts/console/cases";

export const CASE_FEATURES = {
  /** Work cases: claim, log calls, reply, add notes, close your own, link a customer. */
  work: "CLAIM_CASES_STAFF_FEATURE_ENABLEMENT",
  /** Hand a case to someone else; close anyone's. */
  reassign: "REASSIGN_CASES_STAFF_FEATURE_ENABLEMENT",
  searchAccounts: "SEARCH_ACCOUNTS_STAFF_FEATURE_ENABLEMENT",
  viewAccounts: "VIEW_ACCOUNTS_STAFF_FEATURE_ENABLEMENT",
} as const satisfies Record<string, StaffFeatureId>;

export type CaseControls = {
  /** « Prendre »: only while nobody owns it. */
  claim: boolean;
  /** « Réattribuer », with REASSIGN_CASES. */
  reassign: boolean;
  /** The team list (GET /console/staff) is readable: offer a picker, not an ID field. */
  staffPicker: boolean;
  note: boolean;
  logCall: boolean;
  /** « Répondre »: the owner only. */
  reply: boolean;
  /** Why « Répondre » isn't offered, when working cases is allowed. */
  replyBlocked: "closed" | "unassigned" | "notOwner" | null;
  close: boolean;
  link: boolean;
  /** The customer picker searches accounts; without it, an account id. */
  customerSearch: boolean;
  /** The header links to the customer's page. */
  customerPage: boolean;
};

const hasAny = (features: readonly StaffFeatureId[], wanted: readonly StaffFeatureId[]) =>
  wanted.some((feature) => features.includes(feature));

export function caseControls(me: GetStaffMeResponse, detail: SupportCaseDetail): CaseControls {
  const has = (feature: StaffFeatureId) => me.features.includes(feature);
  const work = has(CASE_FEATURES.work);
  const open = detail.status !== "closed";
  const mine = detail.owner?.staffMemberId === me.staffMember.staffMemberId;
  return {
    claim: work && open && detail.owner === null,
    reassign: has(CASE_FEATURES.reassign) && open,
    staffPicker: hasAny(me.features, TEAM_SCREEN_FEATURES),
    note: work,
    logCall: work && open,
    reply: work && open && mine,
    replyBlocked: !work ? null : !open ? "closed" : detail.owner === null ? "unassigned" : mine ? null : "notOwner",
    close: work && open && (mine || has(CASE_FEATURES.reassign)),
    link: work,
    customerSearch: has(CASE_FEATURES.searchAccounts),
    customerPage: has(CASE_FEATURES.viewAccounts) && detail.customer !== null,
  };
}

/**
 * Whether the owner can reply: replies go in the app, with a notification to
 * the customer (never by SMS), so the case needs a customer account. An
 * unknown caller is linked first, or called back.
 */
export const canReplyInApp = (detail: SupportCaseDetail): boolean => detail.customer !== null;

/** « + Noter un appel » in the queue. */
export const canOpenCase = (me: GetStaffMeResponse) => me.features.includes(CASE_FEATURES.work);
